/**
 * ManualEntryDialog — key in a single transaction that isn't on any
 * bank statement (PayPal, cash, refund, etc.). Marked `source='manual'`
 * server-side so it can be distinguished later.
 */
import { useEffect, useRef, useState } from "react";
import { X, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { createManualTransaction } from "@/lib/operations/api";

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function ManualEntryDialog({ config, onClose, onCreated }) {
  const [date, setDate] = useState(todayISO());
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [isIncome, setIsIncome] = useState(false);
  const [category, setCategory] = useState("");
  const [costCentre, setCostCentre] = useState("");
  const [saving, setSaving] = useState(false);
  const descRef = useRef(null);

  useEffect(() => {
    descRef.current?.focus();
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const canSave = date && description.trim() && amount && category;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSave || saving) return;
    const num = parseFloat(amount);
    if (Number.isNaN(num) || num === 0) {
      toast.error("Enter a non-zero amount");
      return;
    }
    setSaving(true);
    try {
      const signed = isIncome ? Math.abs(num) : -Math.abs(num);
      const doc = await createManualTransaction({
        date,
        description: description.trim(),
        amount: signed,
        category,
        cost_centre: costCentre || null,
      });
      toast.success("Entry added");
      onCreated?.(doc);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[85] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center px-0 sm:px-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      data-testid="ops-manual-dialog"
    >
      <div
        className="relative w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl p-6 sm:p-7 pb-8"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 h-9 w-9 rounded-full text-slate-400 hover:text-brand-navy hover:bg-slate-100 flex items-center justify-center"
          aria-label="Close"
          data-testid="ops-manual-close"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="h-11 w-11 rounded-full bg-brand-mist flex items-center justify-center text-brand-navy">
          <Plus className="h-5 w-5" />
        </div>
        <h2 className="mt-3 text-xl sm:text-2xl font-bold font-heading text-brand-navy">Add manual entry</h2>
        <p className="mt-0.5 text-sm text-slate-500">
          For PayPal, cash, refunds, or anything not on the bank feed.
        </p>

        <form onSubmit={submit} className="mt-5 space-y-3">
          {/* Income / Expense toggle */}
          <div className="inline-flex items-center rounded-full bg-slate-100 p-0.5">
            <button
              type="button"
              onClick={() => setIsIncome(false)}
              className={`px-4 h-8 rounded-full text-xs font-semibold transition ${
                !isIncome ? "bg-white text-brand-navy shadow-sm" : "text-slate-500"
              }`}
              data-testid="ops-manual-expense"
            >
              Expense
            </button>
            <button
              type="button"
              onClick={() => setIsIncome(true)}
              className={`px-4 h-8 rounded-full text-xs font-semibold transition ${
                isIncome ? "bg-white text-emerald-600 shadow-sm" : "text-slate-500"
              }`}
              data-testid="ops-manual-income"
            >
              Income
            </button>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="mt-1.5 w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-teal/40 focus:border-brand-teal"
              data-testid="ops-manual-date"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">Description</label>
            <input
              ref={descRef}
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. PayPal — Print T-shirts (Vistaprint)"
              maxLength={240}
              className="mt-1.5 w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-teal/40 focus:border-brand-teal"
              data-testid="ops-manual-desc"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">
              Amount (CAD)
            </label>
            <div className="mt-1.5 relative">
              <span className={`absolute left-3 top-1/2 -translate-y-1/2 font-semibold ${
                isIncome ? "text-emerald-600" : "text-slate-500"
              }`}>
                {isIncome ? "+" : "−"}$
              </span>
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="w-full h-11 rounded-lg border border-slate-200 bg-white pl-10 pr-3 text-brand-navy tabular-nums focus:outline-none focus:ring-2 focus:ring-brand-teal/40 focus:border-brand-teal"
                data-testid="ops-manual-amount"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="mt-1.5 w-full h-11 rounded-lg border border-slate-200 bg-white px-2 text-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-teal/40"
                data-testid="ops-manual-category"
                required
              >
                <option value="">Choose…</option>
                {config.categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">Cost centre</label>
              <select
                value={costCentre}
                onChange={(e) => setCostCentre(e.target.value)}
                className="mt-1.5 w-full h-11 rounded-lg border border-slate-200 bg-white px-2 text-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-teal/40"
                data-testid="ops-manual-cc"
              >
                <option value="">—</option>
                {config.cost_centres.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
          </div>

          <div className="pt-3 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 h-11 rounded-full text-sm font-semibold text-slate-600 hover:bg-slate-100 transition"
              data-testid="ops-manual-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSave || saving}
              className="px-5 h-11 rounded-full bg-brand-teal hover:bg-brand-teal-dark text-white font-semibold flex items-center gap-2 disabled:opacity-50 transition"
              data-testid="ops-manual-save"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Add entry
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
